<template>
  <div class="alert-overlay" v-if="activeAlerts.length > 0">
    <transition-group name="alert-slide">
      <div
        v-for="alert in activeAlerts"
        :key="alert.id"
        class="alert-banner"
        :class="alert.type"
      >
        <div class="alert-icon">{{ alert.icon }}</div>
        <div class="alert-content">
          <div class="alert-title">{{ alert.title }}</div>
          <div class="alert-message">{{ alert.message }}</div>
        </div>
        <div class="alert-timer-bar">
          <div
            class="alert-timer-fill"
            :style="{ animationDuration: alert.duration + 's' }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'AlertOverlay',
  data() {
    return {
      activeAlerts: [],
      alertCounter: 0,
      audioContext: null,
      lastSoundPlayed: 0,
    }
  },
  mounted() {
    this.emitter.on('alert-triggered', this.handleAlert)
  },
  beforeUnmount() {
    this.emitter.off('alert-triggered', this.handleAlert)
    if (this.audioContext) {
      this.audioContext.close()
    }
  },
  methods: {
    handleAlert(alert) {
      const id = ++this.alertCounter
      const duration = alert.duration || 10

      const icons = {
        volume: '\u266A',
        pause: '\u23F8',
        disconnect: '\u26A0',
      }

      const titles = {
        volume: 'Volumen bajo',
        pause: 'Reproductor pausado',
        disconnect: 'Sin conexi\u00f3n',
      }

      const entry = {
        id,
        type: alert.type,
        icon: icons[alert.type] || '\u26A0',
        title: titles[alert.type] || 'Aviso',
        message: alert.message,
        duration,
      }

      this.activeAlerts.push(entry)

      if (this.activeAlerts.length > 3) {
        this.activeAlerts.shift()
      }

      this.playAlertSound()

      setTimeout(() => {
        this.dismiss(id)
      }, duration * 1000)
    },

    dismiss(id) {
      const idx = this.activeAlerts.findIndex(a => a.id === id)
      if (idx !== -1) {
        this.activeAlerts.splice(idx, 1)
      }
    },

    playAlertSound() {
      try {
        const now = Date.now()
        if (now - this.lastSoundPlayed < 1000) return

        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return

        if (!this.audioContext || this.audioContext.state === 'closed') {
          this.audioContext = new AudioCtx()
        }

        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume()
        }

        const ctx = this.audioContext
        const audioNow = ctx.currentTime

        // Primer tono - "ding"
        const osc1 = ctx.createOscillator()
        const gain1 = ctx.createGain()
        osc1.type = 'sine'
        osc1.frequency.setValueAtTime(880, audioNow)
        gain1.gain.setValueAtTime(0, audioNow)
        gain1.gain.linearRampToValueAtTime(0.2, audioNow + 0.01)
        gain1.gain.exponentialRampToValueAtTime(0.001, audioNow + 0.15)
        osc1.connect(gain1)
        gain1.connect(ctx.destination)
        osc1.start(audioNow)
        osc1.stop(audioNow + 0.15)

        // Segundo tono - "ding" más agudo
        const osc2 = ctx.createOscillator()
        const gain2 = ctx.createGain()
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(1175, audioNow + 0.12)
        gain2.gain.setValueAtTime(0, audioNow + 0.12)
        gain2.gain.linearRampToValueAtTime(0.18, audioNow + 0.13)
        gain2.gain.exponentialRampToValueAtTime(0.001, audioNow + 0.3)
        osc2.connect(gain2)
        gain2.connect(ctx.destination)
        osc2.start(audioNow + 0.12)
        osc2.stop(audioNow + 0.3)

        this.lastSoundPlayed = Date.now()
      } catch (e) {
        console.warn('AlertOverlay: no se pudo reproducir sonido', e)
      }
    },
  },
}
</script>

<style scoped>
.alert-overlay {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
  max-width: 380px;
  font-family: 'Montserrat', sans-serif;
}

@media (min-width: 767px) {
  .alert-overlay {
    top: 87px;
  }
}

.alert-banner {
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 0;
  padding: 12px 14px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 280px;
  max-width: 380px;
  pointer-events: auto;
  border-left: 4px solid;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;
}

.alert-banner.volume {
  border-left-color: #ffc409;
}

.alert-banner.pause {
  border-left-color: #50c8ff;
}

.alert-banner.disconnect {
  border-left-color: #ff4961;
}

.alert-icon {
  font-size: 18px;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}

.alert-banner.volume .alert-icon {
  color: #ffd534;
}

.alert-banner.pause .alert-icon {
  color: #50c8ff;
}

.alert-banner.disconnect .alert-icon {
  color: #eb445a;
}

.alert-content {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}

.alert-message {
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  margin: 2px 0 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-timer-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.alert-timer-fill {
  height: 100%;
  width: 100%;
  transform-origin: left;
  animation: alert-countdown linear forwards;
}

.alert-banner.volume .alert-timer-fill {
  background: #ffc409;
}

.alert-banner.pause .alert-timer-fill {
  background: #50c8ff;
}

.alert-banner.disconnect .alert-timer-fill {
  background: #ff4961;
}

@keyframes alert-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

.alert-slide-enter-active {
  animation: alert-slide-in 0.3s ease-out;
}

.alert-slide-leave-active {
  animation: alert-slide-out 0.25s ease-in forwards;
}

@keyframes alert-slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes alert-slide-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
</style>
